import React, { useEffect, useRef, useState } from "react";
import "tui-image-editor/dist/tui-image-editor.css";
import ImageEditor from "tui-image-editor";
import "./styles.scss";

const ImageEditorTest = ({ file, onSave, onClose }) => {
    const editorRef = useRef(null);
    const containerRef = useRef(null);
    const [editorSize, setEditorSize] = useState({ width: 1000, height: 600 });

    // Update editor size based on container (with debounce)
    useEffect(() => {
        let resizeTimeout;

        const updateEditorSize = () => {
            if (resizeTimeout) clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                if (!containerRef.current) return;
                const { offsetWidth, offsetHeight } = containerRef.current;
                setEditorSize((prev) => {
                    // Only update if changed
                    if (prev.width === offsetWidth && prev.height === offsetHeight) {
                        return prev;
                    }
                    return { width: offsetWidth, height: offsetHeight };
                });
            }, 200);
        };

        updateEditorSize();
        window.addEventListener("resize", updateEditorSize);
        return () => {
            window.removeEventListener("resize", updateEditorSize);
            if (resizeTimeout) clearTimeout(resizeTimeout);
        };
    }, []);

    // Initialize / re-initialize editor when `file` or size changes
    useEffect(() => {
        if (!file || !editorSize.width || !editorSize.height) return;

        // Safely destroy existing editor if any
        if (editorRef.current && typeof editorRef.current.destroy === "function") {
            try {
                editorRef.current.destroy();
            } catch (err) {
                console.warn("Editor destroy failed gracefully:", err);
            }
            editorRef.current = null;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const editor = new ImageEditor(document.querySelector("#tui-editor"), {
                includeUI: {
                    loadImage: {
                        path: reader.result,
                        name: file.name,
                    },
                    menu: ["crop", "flip", "rotate", "draw", "shape", "icon", "text", "filter"],
                    initMenu: "draw",
                    uiSize: {
                        width: `${editorSize.width}px`,
                        height: `${editorSize.height}px`,
                    },
                    menuBarPosition: "right",
                },
                cssMaxWidth: editorSize.width - 50,
                cssMaxHeight: editorSize.height - 100,
                usageStatistics: false,
            });

            editorRef.current = editor;

            // Hide TUI branding logo
            const logo = document.querySelector(".tui-image-editor-header-logo");
            if (logo) logo.style.display = "none";
            const headerBtns = document.querySelector(".tui-image-editor-header-buttons");
            if (headerBtns) headerBtns.style.display = "none";

            // Remove Delete / Delete All Buttons after a short delay (to ensure they are in DOM)
            setTimeout(() => {
                const deleteBtn = document.querySelector(".tie-btn-delete");
                const deleteAllBtn = document.querySelector(".tie-btn-deleteAll");
                if (deleteBtn) deleteBtn.style.display = "none";
                if (deleteAllBtn) deleteAllBtn.style.display = "none";
            }, 300);

            // Handle color picker visibility (you said portrait issue is fixed, so skipping deeper changes)
            const showColorPalette = () => {
                setTimeout(() => {
                    const colorBtn = document.querySelector(".tui-colorpicker-button");
                    if (colorBtn) {
                        colorBtn.click();
                    }
                    const colorSquare = document.querySelector(".tui-colorpicker-palette-hex");
                    if (colorSquare) {
                        colorSquare.click();
                    }
                }, 200);
            };

            editor.on("objectActivated", showColorPalette);
            editor.on("menuChanged", (menu) => {
                if (["draw", "text", "shape"].includes(menu.menu)) {
                    showColorPalette();
                }
            });
        };

        reader.readAsDataURL(file);

        // Cleanup on unmount
        return () => {
            if (editorRef.current && typeof editorRef.current.destroy === "function") {
                try {
                    editorRef.current.destroy();
                } catch (err) {
                    console.warn("Cleanup: editor destroy failed", err);
                }
                editorRef.current = null;
            }
        };
    }, [file, editorSize]);

    const handleSave = async () => {
        if (!editorRef.current || typeof editorRef.current.toDataURL !== "function") {
            return;
        }

        const dataUrl = editorRef.current.toDataURL();
        const img = new Image();
        img.src = dataUrl;

        img.onload = async () => {
            const maxWidth = 500;
            const maxHeight = 500;
            const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
            const width = img.width * ratio;
            const height = img.height * ratio;

            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, width, height);

            const resizedDataUrl = canvas.toDataURL();
            const blob = await (await fetch(resizedDataUrl)).blob();
            const originalName = file?.name || "image.jpg";
            const editedFile = new File([blob], originalName, { type: blob.type });

            onSave(editedFile);
            onClose();
        };
    };

    return (
        <div
            ref={containerRef}
            style={{
                width: "100%",
                height: "100%",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <div id="tui-editor" style={{ flex: 1, width: "100%", height: "100%" }} />
            <div
                style={{
                    margin: "10px 0",
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                }}
            >
                <button type="button" onClick={handleSave} className="editor-btn">
                    Save & Upload
                </button>
                <button
                    type="button"
                    className="button"
                    onClick={onClose}
                    style={{ marginLeft: "10px" }}
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};

export default ImageEditorTest;
